import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
    DataGridTable,
    GridAction,
    ConfirmationPopup,
    notifyToast,
    ToastType,
    FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { saveGrossBranch } from "../api-handler/api-handler";
import { useGrossBranchFormData } from "../../../hooks/useGrossBranchFormData";

const GrossBranchListForm = () => {
    /**************************************************
    //Form handlers & hooks
    **************************************************/
    const { t } = useTranslation();
    const navigate = useNavigate();
    const spinnerCtx = useContext(DisplaySpinnerContext);
    const settingsConfig = useSelector(selectSessionConfig);

    const createGrossBranchLookupData = useGrossBranchFormData(settingsConfig.businessCode, settingsConfig.countryCode);

    /**************************************************
    //Form state variables
    **************************************************/
    const [grossBranchRecordList, setGrossBranchRecordList] = useState([]);
    const [dataGridPageSize, setDataGridPageSize] = useState(10);
    const [totalGrossBranch, setTotalGrossBranch] = useState(0);
    const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);


    /**************************************************
    //Event definitions
    **************************************************/

    //on click of add icon for creating gross branch
    const handleCreateGrossBranchClick = () => {
        navigate("/grossbranch");
    };

    //onclick of edit gross branch
    const handleEditGrossBranchClick = async (param) => {
        let _navigationData = param.recordResponse;
        delete _navigationData.CreatedDate;
        delete _navigationData.ModifiedDate;
        delete _navigationData.CountryCode;
        delete _navigationData.BusinessCode;

        navigate(
            {
                pathname: "/grossbranch",
                search: createSearchParams({
                    GrossBranchId: param.recordResponse.GrossBranchId,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    //onclick of confirm of delete popup
    const handleConfirm = async () => {
        await deleteGrossBranchConfig(selectedRow);
        setPopupOpen(false);
        setSelectedRow(null);
    };

    //on click of cancel of delete popup
    const handleCancel = () => {
        setSelectedRow(null);
        setPopupOpen(false);
    };

    //onClick of delete in list
    const showDeleteGrossBranchPopup = (event) => {
        try {
            setSelectedRow(event);
            setPopupOpen(true);
        } catch (err) {
            alert(err);
        }
    };

    //on page change
    const handlePageSizeChange = (newPageSize) => {
        setDataGridPageSize(newPageSize);
    };

    //show review modal
    const handleShowSelectedGrossBranchChanges = (event) => {
        let _navigationData = { previousPath: "/grossbranchlist" };
        navigate(
            {
                pathname: "/reviewdetails",
                search: createSearchParams({
                    reviewFilter: `&locationkey=${event.id}`,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    /**************************************************
    //Fetch API & utility
    **************************************************/


    const setGrossBranchList = (grossBranchList) => {
        try {
            grossBranchList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
            let _grossBranchList = grossBranchList.map((item) => {
                return {
                    id: item.GrossBranchId,
                    serviceTypeName: item.ServiceName,
                    sectorName: item.SectorName,
                    branchName: item.BranchName,
                    contractsGrossMargin: item.ContractsGrossMargin,
                    jobsGrossMargin: item.JobsGrossMargin,
                    coefficientWarranty: item.CoefficientWarranty,
                    recordResponse: item,
                };
            });

            setGrossBranchRecordList(_grossBranchList);
            setTotalGrossBranch(_grossBranchList.length);
            spinnerCtx.setLoadingIndicator(false);
        } catch (err) {
            spinnerCtx.setLoadingIndicator(false);
            alert(err);
        }
    };

    const FetchGrossBranchList = async () => {
        try {
            let _lists = await PricingPortalAPI.getListWithPagination(
                settingsConfig.businessCode,
                settingsConfig.countryCode,
                "getGrossBranchList",
                "grossbranch"
            );
            setGrossBranchList(_lists);
        } catch (err) {
            alert(err);
        }
    };

    const formPreviousDataForDelete = (param) => {
        try {
            let _previousState = param.recordResponse;
            _previousState.Deleted = true;
            delete _previousState.CreatedDate;
            delete _previousState.ModifiedDate;
            delete _previousState.CountryCode;
            delete _previousState.BusinessCode;
            delete _previousState.Version;
            delete _previousState.GrossBranchId;
            delete _previousState.Status;

            createGrossBranchLookupData.serviceTypeList.forEach((item) => {
                if (item.ServiceTypeId === _previousState.TypeId) {
                    _previousState.ServiceType = {
                        id: item.ServiceTypeId,
                        label: item.ServiceName,
                    };
                }
            });

            createGrossBranchLookupData.marketSectorList.forEach((item) => {
                if (item.SectorId === _previousState.SectorId) {
                    _previousState.Sector = {
                        id: item.SectorId,
                        label: item.SectorName,
                    };
                }
            });

            createGrossBranchLookupData.branchList.forEach((item) => {
                if (item.BranchId === _previousState.BranchId) {
                    _previousState.Branch = {
                        id: item.BranchId,
                        label: item.BranchName,
                    };
                }
            });

            return _previousState ? _previousState : null;
        } catch (e) {
            alert(e);
        }
    };

    //Delete API call
    const deleteGrossBranchConfig = async (param) => {
        try {
            spinnerCtx.setLoadingIndicator(true);
            let EditGrossBranchId = param.id;
            const status = await saveGrossBranch(formPreviousDataForDelete(param), {
                EditGrossBranchId,
                settingsConfig
            });

            if (status?.responseStatus === 200) {
                notifyToast("Sent for Approval", ToastType.SUCCESS);
                await FetchGrossBranchList();
            } else {
                notifyToast(status.errorMessage ? status.errorMessage : "Gross Branch deletion failed", ToastType.ERROR);
                spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            alert(err);
        }
    };

    /**************************************************
    //JSX delcaration
    **************************************************/

    //data grid schema
    const dataGridSchema = [
        {
            field: "serviceTypeName",
            headerName: t("grossBranchList.serviceTypeName"),
            flex: 2,
        },
        {
            field: "sectorName",
            headerName: t("grossBranchList.sectorName"),
            flex: 2,
        },
        {
            field: "branchName",
            headerName: t("grossBranchList.branchName"),
            flex: 1,
        },
        {
            field: "contractsGrossMargin",
            headerName: t("grossBranchList.contractsGrossMargin"),
            flex: 1,
        },
        {
            field: "jobsGrossMargin",
            headerName: t("grossBranchList.jobsGrossMargin"),
            flex: 1,
        },
        {
            field: "coefficientWarranty",
            headerName: t("grossBranchList.coefficientWarranty"),
            flex: 1,
        },
        {
            field: "recordResponse",
            hide: true,
        },
        {
            field: "action",
            headerName: t,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <GridAction actionsConfig={actionConfigs} row={params.row} />
                );
            },
        },
    ];

    //button configs for add and download list
    const buttonConfigs = [
        {
            buttonText: <AddIcon />,
            buttonType: "filled",
            onClick: handleCreateGrossBranchClick,
            buttonColor: "#A62B23",
            textColor: "#fff",
            action: "create"
        },
        {
            buttonText: <SystemUpdateAltIcon />,
            buttonType: "outlined",
            onClick: () => {
                AppUtility.handleDownload(grossBranchRecordList, "grossbranchlist.xlsx");
            },
            buttonColor: "#A62B23",
            textColor: "#A62B23",
            action: "download"
        },
    ];

    //action configs for edit and delete record
    const actionConfigs = [
        {
            onClick: handleShowSelectedGrossBranchChanges,
            action: "review"
        },
        {
            icon: MdCreate,
            onClick: handleEditGrossBranchClick,
            buttonColor: "#EEEEE",
            action: "edit"
        },
        {
            icon: MdDelete,
            onClick: showDeleteGrossBranchPopup,
            buttonColor: "#A62B23",
            action: "delete"
        },
    ];


    /**************************************************
    //Form on load useEffect
    **************************************************/
    useEffect(() => {

        spinnerCtx.setLoadingIndicator(true);
        FetchGrossBranchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <FormPage isList={true}>
            <DataGridTable
                rows={grossBranchRecordList}
                columns={dataGridSchema}
                pageSize={dataGridPageSize}
                pageSizeOptions={gridPageSizeOptions}
                onPageSizeChange={handlePageSizeChange}
                totalRowCount={totalGrossBranch}
                toolbarHeader={t("grossBranchList.header")}
                toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
                buttonConfigs={buttonConfigs}
            />
            <ConfirmationPopup
                isOpen={popupOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                confirmationText={t("popup.deleteGrossBranchConfirmationMessage")}
                eventObj={selectedRow}
            />
        </FormPage>
    );
};

export default GrossBranchListForm;
